/**
 * 🚗 VEHICLE DISCOUNT CAMPAIGNS PAGE
 * Admin creates campaigns for vehicle discounts (Black Friday, Summer Sale, etc.)
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Stack,
  FormControlLabel,
  Switch,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  DirectionsCar as VehicleIcon,
  TrendingUp as TrendingIcon,
  AttachMoney as MoneyIcon,
  PlayArrow as StartIcon,
  Stop as StopIcon,
} from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import vehicleCampaignService, {
  VehicleCampaign,
  VehicleCampaignFormData,
  VehicleCampaignStats,
} from '@/services/vehicleCampaignService';

export const VehicleCampaignsPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [campaigns, setCampaigns] = useState<VehicleCampaign[]>([]);
  const [stats, setStats] = useState<VehicleCampaignStats>({
    totalCampaigns: 0,
    activeCampaigns: 0,
    upcomingCampaigns: 0,
    expiredCampaigns: 0,
  });
  const [filterStatus, setFilterStatus] = useState<'active' | 'upcoming' | 'expired' | 'all'>('all');
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<VehicleCampaign | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState<VehicleCampaign | null>(null);
  
  // Form data
  const [formData, setFormData] = useState<Partial<VehicleCampaignFormData>>({
    name: '',
    description: '',
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    isActive: false,
  });

  useEffect(() => {
    fetchCampaigns();
    fetchStats();
  }, [filterStatus]);

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const result = await vehicleCampaignService.getCampaigns({
        status: filterStatus,
      });

      if (result.success && result.data?.campaigns) {
        setCampaigns(result.data.campaigns);
      } else {
        setCampaigns([]);
        if (!result.success) {
          toast.error(result.error || 'Failed to load campaigns');
        }
      }
    } catch (error) {
      console.error('Error in fetchCampaigns:', error);
      setCampaigns([]);
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const statsData = await vehicleCampaignService.getStats();
      if (statsData) {
        setStats(statsData);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
      // Keep default zero stats on error
    }
  };

  const getAdminUserId = (): string => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        return user.id || user.userId || 'admin-default';
      } catch (e) {
        return 'admin-default';
      }
    }
    return 'admin-default';
  };

  const handleSaveCampaign = async () => {
    if (!formData.name || !formData.startDate || !formData.endDate) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const adminUserId = getAdminUserId();
      const campaignData: VehicleCampaignFormData = {
        name: formData.name!,
        description: formData.description || '',
        startDate: formData.startDate!,
        endDate: formData.endDate!,
        isActive: formData.isActive || false,
        adminUserId,
      };

      const result = editingCampaign
        ? await vehicleCampaignService.updateCampaign(editingCampaign.id, campaignData)
        : await vehicleCampaignService.createCampaign(campaignData);

      if (result.success) {
        toast.success(`Campaign ${editingCampaign ? 'updated' : 'created'} successfully`);
        setDialogOpen(false);
        resetForm();
        fetchCampaigns();
        fetchStats();
      } else {
        toast.error(result.error || 'Failed to save campaign');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCampaign = async () => {
    if (!campaignToDelete) return;

    setLoading(true);
    try {
      const result = await vehicleCampaignService.deleteCampaign(campaignToDelete.id);

      if (result.success) {
        toast.success('Campaign deleted successfully');
        setDeleteDialogOpen(false);
        setCampaignToDelete(null);
        fetchCampaigns();
        fetchStats();
      } else {
        toast.error(result.error || 'Failed to delete campaign');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleToggleCampaign = async (campaign: VehicleCampaign) => {
    setLoading(true);
    try {
      const adminUserId = getAdminUserId();
      const result = await vehicleCampaignService.toggleCampaign(
        campaign.id,
        !campaign.isActive,
        adminUserId
      );

      if (result.success) {
        toast.success(`Campaign ${!campaign.isActive ? 'activated' : 'deactivated'}`);
        fetchCampaigns();
        fetchStats();
      } else {
        toast.error(result.error || 'Failed to toggle campaign');
      }
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    resetForm();
    setEditingCampaign(null);
    setDialogOpen(true);
  };

  const openEditDialog = (campaign: VehicleCampaign) => {
    setEditingCampaign(campaign);
    setFormData({
      name: campaign.name,
      description: campaign.description,
      startDate: campaign.startDate.split('T')[0],
      endDate: campaign.endDate.split('T')[0],
      isActive: campaign.isActive,
    });
    setDialogOpen(true);
  };

  const openDeleteDialog = (campaign: VehicleCampaign) => {
    setCampaignToDelete(campaign);
    setDeleteDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      startDate: new Date().toISOString().split('T')[0],
      endDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      isActive: false,
    });
  };

  const getCampaignStatus = (campaign: VehicleCampaign): 'active' | 'upcoming' | 'expired' => {
    const now = new Date();
    const start = new Date(campaign.startDate);
    const end = new Date(campaign.endDate);

    if (!campaign.isActive) return 'expired';
    if (now < start) return 'upcoming';
    if (now > end) return 'expired';
    return 'active';
  };

  const getStatusChip = (campaign: VehicleCampaign) => {
    const status = getCampaignStatus(campaign);
    const colorMap = {
      active: 'success' as const,
      upcoming: 'info' as const,
      expired: 'default' as const,
    };

    return <Chip label={status.charAt(0).toUpperCase() + status.slice(1)} color={colorMap[status]} size="small" />;
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VehicleIcon fontSize="large" />
            Vehicle Discount Campaigns
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Create and manage vehicle discount campaigns (Black Friday, Summer Sale, etc.)
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              fetchCampaigns();
              fetchStats();
            }}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreateDialog}
          >
            Create Campaign
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <VehicleIcon color="primary" sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="h4" fontWeight="bold">{stats.totalCampaigns}</Typography>
                    <Typography variant="body2" color="text.secondary">Total Campaigns</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <TrendingIcon color="success" sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="h4" fontWeight="bold" color="success.main">{stats.activeCampaigns}</Typography>
                    <Typography variant="body2" color="text.secondary">Active Now</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <MoneyIcon color="info" sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="h4" fontWeight="bold" color="info.main">{stats.upcomingCampaigns}</Typography>
                    <Typography variant="body2" color="text.secondary">Upcoming</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <StopIcon color="disabled" sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="h4" fontWeight="bold">{stats.expiredCampaigns}</Typography>
                    <Typography variant="body2" color="text.secondary">Expired</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Filter by Status</InputLabel>
          <Select
            value={filterStatus}
            label="Filter by Status"
            onChange={(e) => setFilterStatus(e.target.value as any)}
          >
            <MenuItem value="all">All Campaigns</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="upcoming">Upcoming</MenuItem>
            <MenuItem value="expired">Expired</MenuItem>
          </Select>
        </FormControl>
      </Paper>

      {/* Campaigns Table */}
      <TableContainer component={Paper}>
        {loading && <CircularProgress sx={{ m: 2 }} />}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Campaign Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Start Date</TableCell>
              <TableCell>End Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Active</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {campaigns.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                    No campaigns found. Create your first campaign to get started!
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {campaigns.map((campaign) => (
              <TableRow key={campaign.id}>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">{campaign.name}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{campaign.description}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{new Date(campaign.startDate).toLocaleDateString()}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{new Date(campaign.endDate).toLocaleDateString()}</Typography>
                </TableCell>
                <TableCell>{getStatusChip(campaign)}</TableCell>
                <TableCell>
                  <Tooltip title={campaign.isActive ? "Click to deactivate" : "Click to activate"}>
                    <IconButton
                      size="small"
                      onClick={() => handleToggleCampaign(campaign)}
                      color={campaign.isActive ? "success" : "default"}
                    >
                      {campaign.isActive ? <StartIcon /> : <StopIcon />}
                    </IconButton>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEditDialog(campaign)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => openDeleteDialog(campaign)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingCampaign ? 'Edit Campaign' : 'Create New Campaign'}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField
              label="Campaign Name"
              fullWidth
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Black Friday Vehicle Sale"
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe the campaign..."
            />
            <TextField
              label="Start Date"
              type="date"
              fullWidth
              required
              value={formData.startDate}
              onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End Date"
              type="date"
              fullWidth
              required
              value={formData.endDate}
              onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive || false}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveCampaign} variant="contained" disabled={loading}>
            {editingCampaign ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Campaign?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{campaignToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteCampaign} color="error" variant="contained" disabled={loading}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VehicleCampaignsPage;
